/**
 * ימים מיוחדים קרובים, והצעה מה לעשות איתם.
 *
 * מציע ואינו סוגר. סגירה מוחקת שיבוצים, ולוח שנה חיצוני אינו
 * יודע אם הסניף פתוח בחול המועד או אם הרשת החליטה דווקא לפתוח
 * בערב חג. לכן כל יום כאן הוא כפתור שממלא את הטופס מראש, והמנהל
 * משנה ומאשר.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, EyeOff, RefreshCw, Sparkles } from 'lucide-react';
import { Button, EmptyState, Panel, Skeleton } from '@/design/components';
import { useSettings } from '@/access/SettingsContext';
import { supabase } from '@/lib/supabase';
import { formatDate, toDateKey, weekdayName } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { functionErrorMessage } from '@/lib/functions';
import {
  readDefault,
  suggestionFor,
  SUGGESTION_LABEL,
  useSpecialDayMutations,
  useSpecialDays,
  type ClosureDefault,
  type SpecialDay,
} from './specialDaysApi';

export function SpecialDaysPanel({
  onCreate,
}: {
  /** פותח את טופס הכלל, מלא מראש לפי ההצעה. */
  onCreate: (day: SpecialDay, suggestion: ClosureDefault) => void;
}) {
  const settings = useSettings();
  const today = toDateKey(new Date());
  const days = useSpecialDays(today);
  const queryClient = useQueryClient();
  const { setHidden } = useSpecialDayMutations();

  const defaults = {
    erev: readDefault(settings.raw['schedule.erev_closes'], 'afternoon'),
    holiday: readDefault(settings.raw['schedule.holiday_closes'], 'all_day'),
  };

  const sync = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke('sync-special-days', {
        body: { years: [new Date().getFullYear(), new Date().getFullYear() + 1] },
      });
      if (error) throw new Error(await functionErrorMessage(error));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['special-days'] }),
  });

  const all = days.data ?? [];
  const upcoming = all.filter((day) => !day.is_hidden).slice(0, 12);
  const hidden = all.filter((day) => day.is_hidden);

  return (
    <Panel
      title="ימים מיוחדים קרובים"
      actions={
        <Button variant="secondary" disabled={sync.isPending} onClick={() => sync.mutate()}>
          <RefreshCw size={18} strokeWidth={1.75} aria-hidden="true" />
          {sync.isPending ? 'מסנכרן' : 'עדכון לוח החגים'}
        </Button>
      }
    >
      <div className="grid gap-3">
        {sync.isError && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {errorMessage(sync.error)}
          </p>
        )}

        {days.isPending && <Skeleton className="h-24" />}

        {days.isError && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            לא הצלחנו לטעון את הימים המיוחדים. {errorMessage(days.error)}
          </p>
        )}

        {days.data && upcoming.length === 0 && (
          <EmptyState
            title="אין ימים מיוחדים"
            description="אפשר למשוך את לוח החגים, או להוסיף ימים משלכם."
          />
        )}

        {upcoming.length > 0 && (
          <ul className="m-0 grid list-none gap-1 p-0">
            {upcoming.map((day) => {
              const suggestion = suggestionFor(day, defaults);
              return (
                <li
                  key={day.id}
                  className="flex flex-wrap items-center gap-2 rounded-sm border border-line bg-cream p-2"
                >
                  <span className="grid min-w-0 grow gap-0.5">
                    <span className="text-body-sm font-medium text-ink">
                      {day.name}
                      {day.kind === 'erev' && <span className="text-muted"> · ערב חג</span>}
                    </span>
                    <span className="text-label text-muted">
                      {weekdayName(day.date)}, {formatDate(day.date)}
                      {day.source === 'manual' && ' · נוסף ידנית'}
                    </span>
                  </span>

                  <Button variant="secondary" onClick={() => onCreate(day, suggestion)}>
                    <CalendarPlus size={18} strokeWidth={1.75} aria-hidden="true" />
                    {suggestion === 'none' ? 'יצירת כלל' : SUGGESTION_LABEL[suggestion]}
                  </Button>

                  <button
                    type="button"
                    aria-label={`הסתרת ${day.name}`}
                    title="הסתרה"
                    onClick={() => setHidden.mutate({ id: day.id, hidden: true })}
                    className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-muted hover:bg-raised hover:text-ink"
                  >
                    <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {hidden.length > 0 && (
          <details className="rounded-sm border border-line bg-cream p-2">
            <summary className="cursor-pointer text-label text-muted">
              מוסתרים ({hidden.length})
            </summary>
            <ul className="m-0 mt-2 grid list-none gap-1 p-0">
              {hidden.map((day) => (
                <li key={day.id} className="flex items-center gap-2">
                  <span className="min-w-0 grow truncate text-label text-muted">
                    {day.name} · {formatDate(day.date)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setHidden.mutate({ id: day.id, hidden: false })}
                    className="min-h-9 cursor-pointer rounded-sm border border-muted bg-transparent px-2 text-label text-ink hover:bg-raised"
                  >
                    החזרה לרשימה
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}

        <p className="m-0 flex items-start gap-2 text-label text-muted">
          <Sparkles size={14} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>
            ההצעה היא ברירת מחדל בלבד — אפשר לשנות אותה בטופס לפני השמירה, וגם לקבוע ברירת
            מחדל אחרת לרשת בהגדרות. לוח החגים מ{' '}
            <a href="https://www.hebcal.com" target="_blank" rel="noreferrer" className="text-ink">
              Hebcal
            </a>
            , ברישיון CC BY 4.0.
          </span>
        </p>
      </div>
    </Panel>
  );
}
